import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import type { FirstRequest, FirstRequestsPage } from "../types";

/**
 * First-ask deep dive — what users asked for first, what atula did with it, and
 * whether they came back.
 *
 * Reached from a link at the bottom of the Analytics tab, not from a tab of its
 * own: it answers a question that was asked once, rather than a number you watch.
 *
 * WHY THIS IS A LIVE VIEW AND NOT A STATIC PAGE. The first draft was a generated
 * HTML file served by nginx, which meant 45 users' verbatim messages committed to
 * this repo — permanently, in every clone, long after the Firestore documents they
 * came from are deleted. Quotes are fetched per view instead, from
 * `/admin/analytics/first-requests`, so they live in exactly one place with one
 * deletion path, and an admin has to be signed in to see them. Nothing personal is
 * in the bundle.
 *
 * What IS hard-coded is aggregate: counts and percentages from the 2026-10-26
 * analysis that need the whole population (greeting-only users, the
 * why-no-session decomposition), which no endpoint exposes. Statistics are not
 * personal data. Anything derivable from the live feed is recomputed on every
 * load and labelled as such, so the two can be told apart.
 */

/** Findings from the 2026-10-26 pass that need population-wide thread analysis. */
const SNAPSHOT = {
  date: "2026-10-26",
  cohort: 74,
  greetingOnly: 29,
  greetingStayed: 1,
  greetingOneMessage: 25,
  segments: [
    ["first-day churn & no engagement", 43],
    ["first-day churn & engaged", 6],
    ["2-3 day use", 21],
    ["4+ day use", 4],
  ] as [string, number][],
  // Where each user's path to a live session stopped.
  stopped: [
    ["A. never stated a goal — nothing to offer against", 30, 3],
    ["B. stated a goal, atula never offered a session", 9, 33],
    ["C1. offered, user never replied again", 10, 10],
    ["C3. offered, user deferred", 4, 25],
    ["C4. offered, user changed the subject", 6, 83],
    ["E1. got a link, never spoke in the room", 2, 100],
    ["F. joined a session", 13, 92],
  ] as [string, number, number][],
  sessionsCreatedUsers: 16,
  sessionsJoinedUsers: 13,
  // Paraphrased deliberately — the capability is the finding, the user's words
  // are not needed to act on it.
  gaps: [
    ["See a screen-shared diagram and critique it", "system design mock", "most engaged user"],
    ["Generate PDFs, images or infographics", "asked twice", "6 active days"],
    ["Edit a portfolio page or GitHub repo directly", "", "6 active days"],
    ["Make wallpapers", "", "2 active days"],
    ["Post into a group chat", "asked 3 times", "3 active days"],
    ["Email the session link instead of texting it", "", "2 active days"],
    ["Pause the trial", "", "churned"],
  ] as [string, string, string][],
};

const pct = (a: number, b: number) => (b ? `${Math.round((100 * a) / b)}%` : "—");

function Tile({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="tile">
      <div className="tile-label">{label}</div>
      <div className="tile-value">{value}</div>
      <div className="tile-note">{note}</div>
    </div>
  );
}

function AskTable({ items }: { items: FirstRequest[] }) {
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>First ask (verbatim)</th>
            <th className="right nowrap">Active</th>
            <th className="right nowrap">Stayed</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={`${it.user_id}-${i}`}>
              <td>
                {it.missing ? (
                  <span className="muted">(no longer in the thread)</span>
                ) : (
                  it.text
                )}
                {it.stale && (
                  <div className="small muted">⚠ edited since it was classified</div>
                )}
                <div className="small muted">
                  <code>{it.user_id}</code>
                </div>
              </td>
              <td className="right nowrap">
                {it.active_days}
                <span className="muted">/{it.days_since_activation}d</span>
              </td>
              <td className="right nowrap">
                <span className={`badge ${it.active_days >= 2 ? "ok" : ""}`}>
                  {it.active_days >= 2 ? "stayed" : "left"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FirstAskReport() {
  const [page, setPage] = useState<FirstRequestsPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .analyticsFirstRequests("2026-10-10", "", "production")
      .then((p) => !cancelled && setPage(p))
      .catch((e) =>
        !cancelled &&
        setError(e instanceof Error ? e.message : "Could not load first asks."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const live = useMemo(() => {
    const items = page?.items || [];
    const stayed = items.filter((i) => i.active_days >= 2).length;
    const byCat = new Map<string, FirstRequest[]>();
    for (const it of items) {
      const k = it.label || it.category;
      byCat.set(k, [...(byCat.get(k) || []), it]);
    }
    const cats = [...byCat.entries()].sort((a, b) => b[1].length - a[1].length);
    const short = items.filter((i) => i.text.length < 40);
    return { items, stayed, cats, short };
  }, [page]);

  const s = SNAPSHOT;
  const askTotal = live.items.length;

  return (
    <section>
      <div className="section-head">
        <h2 style={{ margin: 0 }}>First-ask deep dive</h2>
        <a className="navlink" href="/analytics">← Analytics</a>
      </div>
      <div className="small muted" style={{ marginBottom: "1rem" }}>
        What users asked for first, what atula did with it, and whether they came
        back. Cohort: real users who activated on or after 2026-10-10.{" "}
        <strong>Quotes and per-category retention are recomputed live</strong>;
        figures needing whole-population thread analysis are from the {s.date} pass
        and are marked.
      </div>

      {loading && <div className="empty muted small">Loading…</div>}
      {error && <div className="error">{error}</div>}

      <div className="tiles">
        <Tile label="Cohort" value={String(s.cohort)} note={`activated 10-10+ · ${s.date}`} />
        <Tile
          label="Stated a goal"
          value={String(askTotal)}
          note={`${pct(live.stayed, askTotal)} returned day 2 · live`}
        />
        <Tile
          label="Greeting only"
          value={String(s.greetingOnly)}
          note={`${pct(s.greetingStayed, s.greetingOnly)} returned day 2 · ${s.date}`}
        />
        <Tile
          label="Joined a session"
          value={String(s.sessionsJoinedUsers)}
          note={`${pct(s.sessionsJoinedUsers, s.cohort)} of the cohort`}
        />
      </div>

      <div className="card">
        <strong>The single cleanest cut in the data.</strong> Whether someone states
        a goal in their first exchange separates{" "}
        <strong>{pct(live.stayed, askTotal)}</strong> day-2 retention from{" "}
        <strong>{pct(s.greetingStayed, s.greetingOnly)}</strong>. Nothing else here
        separates outcomes that sharply — and <strong>0 of {s.greetingOnly}</strong>{" "}
        greeting-only users ever joined a live session, because there is nothing to
        hold a session about. {s.greetingOneMessage} of them sent exactly one
        message, ever.
      </div>

      <h3>Segments <span className="small muted">— {s.date}</span></h3>
      <div className="table-scroll">
        <table>
          <thead>
            <tr><th>Segment</th><th className="right">Users</th><th className="right">Share</th></tr>
          </thead>
          <tbody>
            {s.segments.map(([name, n]) => (
              <tr key={name}>
                <td>{name}</td>
                <td className="right">{n}</td>
                <td className="right muted">{pct(n, s.cohort)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>Retention by first-ask category <span className="small muted">— live</span></h3>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>First ask</th>
              <th className="right">n</th>
              <th className="right">Stayed</th>
            </tr>
          </thead>
          <tbody>
            {live.cats.map(([label, g]) => {
              const st = g.filter((i) => i.active_days >= 2).length;
              return (
                <tr key={label}>
                  <td>{label}</td>
                  <td className="right">{g.length}</td>
                  <td className="right">{st} ({pct(st, g.length)})</td>
                </tr>
              );
            })}
            <tr>
              <td className="muted">(greeting only) <span className="small">— {s.date}</span></td>
              <td className="right muted">{s.greetingOnly}</td>
              <td className="right muted">
                {s.greetingStayed} ({pct(s.greetingStayed, s.greetingOnly)})
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>Why people didn't reach a live session <span className="small muted">— {s.date}</span></h3>
      <div className="small muted" style={{ marginBottom: "0.4rem" }}>
        Every user placed at the first point their path stopped. The funnel dies
        before the offer, not after it: {s.sessionsCreatedUsers} users had a session
        created and {s.sessionsJoinedUsers} joined one (
        {pct(s.sessionsJoinedUsers, s.sessionsCreatedUsers)}).
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Where it stopped</th>
              <th className="right">Users</th>
              <th className="right">Share</th>
              <th className="right">Stayed</th>
            </tr>
          </thead>
          <tbody>
            {s.stopped.map(([name, n, stayedPct]) => (
              <tr key={name}>
                <td>{name}</td>
                <td className="right">{n}</td>
                <td className="right muted">{pct(n, s.cohort)}</td>
                <td className="right muted">{stayedPct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card">
        <strong>53% never got an offer they could act on</strong> — 41% because they
        never said what they wanted, 12% because atula never offered despite a
        stated goal. Of those offered who didn't join, the dominant outcome is{" "}
        <strong>silence, not refusal</strong>: 10 simply never replied again, and
        nobody in this cohort said no outright.
      </div>

      <h3>What users wanted that atula couldn't do <span className="small muted">— {s.date}</span></h3>
      <div className="small muted" style={{ marginBottom: "0.4rem" }}>
        Paraphrased: the capability is the finding. Note <em>who</em> asks — the
        screen-share, PDF and GitHub requests come from the most engaged users in
        the dataset, hitting the ceiling.
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr><th>Capability</th><th>Note</th><th className="right">Asked by</th></tr>
          </thead>
          <tbody>
            {s.gaps.map(([what, note, who]) => (
              <tr key={what}>
                <td>{what}</td>
                <td className="muted small">{note}</td>
                <td className="right muted small nowrap">{who}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>Every first ask, verbatim <span className="small muted">— live</span></h3>
      <div className="small muted" style={{ marginBottom: "0.4rem" }}>
        <code>Active</code> = days with a message / days since activation. Read from
        the live conversation each time this page loads — never stored in a second
        place, never committed to the repo.
      </div>
      {live.cats.map(([label, g]) => {
        const st = g.filter((i) => i.active_days >= 2).length;
        const sorted = [...g].sort((a, b) => b.active_days - a.active_days);
        return (
          <div key={label} style={{ marginTop: "1rem" }}>
            <h4 style={{ margin: "0 0 0.3rem" }}>
              {label}{" "}
              <span className="small muted">
                — n={g.length}, stayed {st} ({pct(st, g.length)})
              </span>
            </h4>
            <AskTable items={sorted} />
          </div>
        );
      })}

      <h3>Method &amp; caveats</h3>
      <div className="card small">
        <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
          <li>
            <strong>n = {s.cohort}.</strong> Sub-segments are single digits. Only the
            greeting cliff ({s.greetingOnly} users) and the offer decomposition carry
            enough weight to act on confidently; per-category rates are direction,
            not measurement.
          </li>
          <li>
            <strong>Attendance is a floor.</strong> Some sessions have neither a
            transcript nor an event record, so "nobody joined" and "we didn't record
            it" cannot be distinguished.
          </li>
          <li>
            <strong>Retention is censored.</strong> Recently activated users have had
            less time to return, which is why active and elapsed days are always
            shown together.
          </li>
          <li>
            <strong>Ask length is a weak predictor</strong> and is not claimed as one
            — bucketing by length is not monotonic. The one clean result: of the{" "}
            {live.short.length} asks under 40 characters, none produced a live
            session.
          </li>
          <li>
            Quotes are fetched live from the engine on every view. No user text is
            stored in this app or in version control.
          </li>
        </ul>
      </div>
    </section>
  );
}
